import React, { useCallback } from "react";
import clsx from "clsx";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Fail, Success } from "@hitachivantara/uikit-react-icons";
import moment from "moment";
import {
  HvInput,
  HvGrid,
  HvDatePicker,
} from "@hitachivantara/uikit-react-core";
import { HvTimePicker } from "@hitachivantara/uikit-react-lab";
import { convertToAmPM } from "lib/utils/datetime";
import { UserPhoneAttribute } from "lib/api";
import useStyles from "./styles";

interface DetailsProps {
  mode?: "add" | "edit";
  isIntegration: boolean;
  phones?: UserPhoneAttribute[];
}

const Details: React.FC<DetailsProps> = ({
  mode = "add",
  isIntegration,
  phones,
}) => {
  const { t } = useTranslation("userAddEdit");
  const classes = useStyles();

  const {
    values,
    setFieldTouched,
    setFieldValue,
    status,
    setStatus,
    errors,
    touched,
  } = useFormikContext<UserDetailsFormData>();

  const { serverSideErrors = {} } = status;

  const parseStatus = useCallback(
    (field: keyof UserDetailsFormData) =>
      serverSideErrors[field] || (errors[field] && touched[field])
        ? "invalid"
        : "valid",
    [errors, serverSideErrors, touched]
  );

  const parseStatusMessage = useCallback(
    (field: keyof UserDetailsFormData) => {
      if (serverSideErrors[field]) {
        return serverSideErrors[field];
      }

      return errors[field] && touched[field] ? errors[field] : "";
    },
    [errors, serverSideErrors, touched]
  );

  const setValueOnForm = useCallback(
    (property: string, selection: unknown) => {
      setFieldValue(property, selection, true);
      // clearServerSideErrors
      setStatus({});
    },
    [setFieldValue, setStatus]
  );

  const setTimeOnBlur = useCallback(
    (selection: HTMLTextAreaElement) => {
      if (selection && selection.placeholder) {
        const fieldToModify =
          selection.placeholder.toLowerCase() === "hh"
            ? "hours"
            : selection.placeholder.toLowerCase() === "mm"
            ? "minutes"
            : "seconds";

        if (
          selection.placeholder.toLowerCase() === "hh" &&
          values.expiration_time &&
          values.expiration_time.period
        ) {
          // eslint-disable-next-line no-param-reassign
          selection.value = convertToAmPM(
            selection.value,
            values.expiration_time.period
          ).toString();
        }
        setFieldValue("expiration_time", {
          ...values.expiration_time,
          [fieldToModify]: parseInt(selection.value, 10),
        });
        // clearServerSideErrors
        setStatus({});
      }
    },
    [setFieldValue, setStatus, values.expiration_time]
  );
  const domain = window.location.hostname.substring(
    0,
    window.location.hostname.indexOf(".")
  );
  const parse = (date: Date | undefined) => {
    if (date) {
      const newDate = moment(date).toDate();
      setValueOnForm("expiration_date", newDate);
    }
  };

  const renderPhones = () => {
    return phones?.map((v, i) => (
      <HvGrid xs={12} md={6}>
        <HvInput
          // eslint-disable-next-line eqeqeq
          className={i % 2 == 0 ? classes.leftInput : classes.input}
          name="phone"
          label={v.id}
          disabled
          value={v.number}
        />
      </HvGrid>
    ));
  };
  return (
    <HvGrid
      className={clsx(classes.root, classes.fields)}
      spacing={0}
      container
      xs={12}
    >
      <>
        {mode === "edit" && (
          <>
            <HvGrid xs={12} md={6}>
              <span title={values.group ? `${values.group}` : "—"}>
                <HvInput
                  className={classes.leftInput}
                  name="group"
                  label={t("details.fields.group")}
                  readOnly
                  value={values.group ? `${values.group}` : "—"}
                />
              </span>
            </HvGrid>
            <HvGrid xs={12} md={6}>
              <span className={classes.enabledLabel}>
                {t("details.fields.enabled")}
              </span>
              {values.enabled && (
                <Success color="green" className={classes.enabledIcon} />
              )}
              {!values.enabled && (
                <Fail color="red" className={classes.enabledIcon} />
              )}
            </HvGrid>
          </>
        )}
        <HvGrid xs={12} md={6}>
          <HvInput
            className={classes.leftInput}
            name="username"
            label={t("details.fields.username")}
            placeholder={t("details.fields.usernamePlaceholder")}
            inputProps={{
              autoComplete: "off",
            }}
            readOnly={!isIntegration}
            required={mode === "add"}
            value={values.username}
            status={parseStatus("username")}
            statusMessage={parseStatusMessage("username")}
            onBlur={() => setFieldTouched("username", true, true)}
            onChange={(evt, value) => {
              setValueOnForm(
                "email",
                `${value?.trim()}_${domain}@hitachivisualization.com`
              );
              setValueOnForm("username", value?.trim());
            }}
          />
        </HvGrid>
        <HvGrid xs={12} md={6}>
          <HvInput
            className={classes.input}
            name="email"
            label={t("details.fields.email")}
            placeholder={t("details.fields.emailPlaceholder")}
            readOnly
            defaultValue={`_${domain}@hitachivisualization.com`}
            value={values.email}
            onBlur={() => setFieldTouched("email", true, true)}
          />
        </HvGrid>
        {mode === "edit" && isIntegration && (
          <HvGrid xs={12} md={6}>
            <HvInput
              className={classes.leftInput}
              name="old_password"
              label={t("details.fields.oldPassword")}
              placeholder={t("details.fields.oldPasswordPlaceholder")}
              inputProps={{
                autoComplete: "off",
              }}
              labels={{
                revealPasswordButtonClickToHideTooltip: t(
                  "details.fields.passwordTooltipHide"
                ),
                revealPasswordButtonClickToShowTooltip: t(
                  "details.fields.passwordTooltipShow"
                ),
              }}
              type="password"
              value={values.old_password}
              required={
                values.password.length > 0 ||
                values.confirmation_password.length > 0
              }
              status={
                values.old_password.length > 0
                  ? parseStatus("old_password")
                  : "valid"
              }
              statusMessage={
                values.old_password.length > 0
                  ? parseStatusMessage("old_password")
                  : "valid"
              }
              onBlur={() => setFieldTouched("old_password", true, true)}
              onChange={(evt, value) => {
                setValueOnForm("old_password", value);
              }}
            />
          </HvGrid>
        )}
        {isIntegration && (
          <HvGrid xs={12} md={6}>
            <HvInput
              className={mode === "edit" ? classes.input : classes.leftInput}
              name="password"
              label={t("details.fields.password")}
              placeholder={t("details.fields.passwordPlaceholder")}
              inputProps={{
                autoComplete: "off",
              }}
              labels={{
                revealPasswordButtonClickToHideTooltip: t(
                  "details.fields.passwordTooltipHide"
                ),
                revealPasswordButtonClickToShowTooltip: t(
                  "details.fields.passwordTooltipShow"
                ),
              }}
              type="password"
              value={values.password}
              required={mode === "add"}
              status={
                mode === "add" || values.password.length > 0
                  ? parseStatus("password")
                  : "valid"
              }
              statusMessage={
                mode === "add" || values.password.length > 0
                  ? parseStatusMessage("password")
                  : "valid"
              }
              onBlur={() => setFieldTouched("password", true, true)}
              onChange={(evt, value) => {
                setValueOnForm("password", value);
              }}
            />
          </HvGrid>
        )}
        {isIntegration && (
          <HvGrid xs={12} md={6}>
            <HvInput
              className={mode !== "edit" ? classes.input : classes.leftInput}
              name="confirmation_password"
              label={t("details.fields.confirmPassword")}
              placeholder={t("details.fields.confirmPasswordPlaceholder")}
              inputProps={{
                autoComplete: "off",
              }}
              labels={{
                revealPasswordButtonClickToHideTooltip: t(
                  "details.fields.passwordTooltipHide"
                ),
                revealPasswordButtonClickToShowTooltip: t(
                  "details.fields.passwordTooltipShow"
                ),
              }}
              type="password"
              value={values.confirmation_password}
              required={mode === "add"}
              status={
                mode === "add" || values.password.length > 0
                  ? parseStatus("confirmation_password")
                  : "valid"
              }
              statusMessage={
                mode === "add" || values.password.length > 0
                  ? parseStatusMessage("confirmation_password")
                  : "valid"
              }
              onBlur={() =>
                setFieldTouched("confirmation_password", true, true)
              }
              onChange={(evt, value) => {
                setValueOnForm("confirmation_password", value);
              }}
            />
          </HvGrid>
        )}
        {isIntegration && (
          <HvGrid xs={12} md={6}>
            <HvDatePicker
              className={mode === "edit" ? classes.input : classes.leftInput}
              id="expiration_date"
              label={t("details.fields.expirationDate")}
              name="expiration_date"
              aria-label="Date"
              placeholder={t("details.fields.expirationDatePlaceholder")}
              value={mode === "edit" ? values.expiration_date : undefined}
              required={mode === "add"}
              statusMessage={t("validation.required")}
              onBlur={() => setFieldTouched("expiration_date", true, true)}
              onChange={(evt) => {
                parse(evt);
              }}
            />
          </HvGrid>
        )}

        {isIntegration && (
          <HvGrid xs={12} md={6}>
            <HvTimePicker
              id="expiration_time"
              name="expiration_time"
              className={mode !== "edit" ? classes.input : classes.leftInput}
              label={t("details.fields.expirationTime")}
              placeholder="00:00:00"
              statusMessage={t("validation.required")}
              disableDefaultValue={mode === "add"}
              defaultValue={values.expiration_time}
              required={mode === "add"}
              status={mode === "add" ? parseStatus("expiration_time") : "valid"}
              value={mode === "edit" ? values.expiration_time : undefined}
              onBlur={(value) => {
                setFieldTouched("expiration_time", true, true);
                setTimeOnBlur(value.target as unknown as HTMLTextAreaElement);
              }}
              onChange={(value) => {
                setValueOnForm("expiration_time", value);
              }}
            />
          </HvGrid>
        )}
        {!isIntegration && renderPhones()}
      </>
    </HvGrid>
  );
};

export default Details;
